import { HttpEvent, HttpHandler, HttpInterceptor, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import { Observable, defer, from, lastValueFrom } from 'rxjs';
import { API_BASE_URL } from '../config/api-config';
import { AuthCookieLockService } from '../services/auth-cookie-lock.service';
import { AuthService } from '../services/auth.service';
import { LoginResponseDto } from '../../shared/models/auth.dto';

/** Hold the cross-tab cookie lock until the response's auth state has been committed. */
export const authCookieInterceptor: HttpInterceptorFn = (req, next) => {
  const path = req.url.split('?')[0];
  const isLogin = req.method === 'POST' && (
    path === `${API_BASE_URL}/auth/login` ||
    path === `${API_BASE_URL}/auth/passkeys/authentication/verify` ||
    path.startsWith(`${API_BASE_URL}/dev/qa/personas/login/`)
  );
  const isRefresh = req.method === 'POST' && path === `${API_BASE_URL}/auth/refresh-access-token`;
  const isSwitch = req.method === 'POST' && path === `${API_BASE_URL}/auth/switch-organization`;
  const isCreateOrg = req.method === 'POST' && path === `${API_BASE_URL}/organizations`;
  const isDemoStart = req.method === 'POST' && path === `${API_BASE_URL}/demo/start`;
  const deletesCookie = (req.method === 'POST' && (
    path === `${API_BASE_URL}/auth/logout` ||
    path === `${API_BASE_URL}/auth/refresh-access-token/logout`
  )) || (req.method === 'PUT' && path === `${API_BASE_URL}/users/me/password`);
  const issuesSession = isLogin || isSwitch || isCreateOrg || isDemoStart;
  if (!issuesSession && !isRefresh && !deletesCookie) {
    return next(req);
  }

  const lock = inject(AuthCookieLockService);
  const auth = inject(AuthService);
  const versionAtRequest = auth.sessionVersion;
  return defer(() => from(lock.runExclusive(async () => {
    // A queued operation from a prior session must not send the new session's cookie.
    if (!isLogin && !auth.isCurrentSessionVersion(versionAtRequest)) {
      throw new Error('Session changed before auth request');
    }
    // AuthInterceptor ran before this lock. A queued refresh may have rotated the
    // access token in the meantime; send the bearer paired with the current cookie.
    const needsCurrentBearer = isSwitch || isCreateOrg || isDemoStart || deletesCookie;
    const currentToken = needsCurrentBearer ? auth.accessTokenForCookieOperation() : null;
    if (path === `${API_BASE_URL}/auth/refresh-access-token/logout` && !currentToken) {
      throw new Error('Current session bearer unavailable');
    }
    const sendReq = needsCurrentBearer
      ? (currentToken
        ? req.clone({ setHeaders: { Authorization: `Bearer ${currentToken}` } })
        : req.clone({ headers: req.headers.delete('Authorization') }))
      : req;
    const response = await lastValueFrom(next(sendReq));
    if (response instanceof HttpResponse) {
      const body = response.body as { session?: unknown; organizationId?: string } | null;
      const issuedSession = isDemoStart ? body?.session : body;
      if (issuesSession && isLoginResponse(issuedSession)) {
        const preferredOrgId = isSwitch || isDemoStart ? body?.organizationId : undefined;
        auth.storeLogin(issuedSession, preferredOrgId);
      } else if (isRefresh && isRefreshResponse(body) && auth.isCurrentSessionVersion(versionAtRequest)) {
        auth.storeAccessToken(body);
      }
    }
    return response;
  })));
};

/** DI ordering: bearer retry must be outside this cookie lock to avoid recursive acquisition. */
@Injectable()
export class AuthCookieInterceptor implements HttpInterceptor {
  private readonly injector = inject(Injector);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return runInInjectionContext(this.injector, () => authCookieInterceptor(req, forwarded => next.handle(forwarded)));
  }
}

function isRefreshResponse(value: unknown): value is { accessToken: string } {
  return !!value && typeof value === 'object' && typeof (value as { accessToken?: unknown }).accessToken === 'string';
}

function isLoginResponse(value: unknown): value is LoginResponseDto {
  if (!value || typeof value !== 'object') return false;
  const response = value as Partial<LoginResponseDto>;
  return typeof response.accessToken === 'string' && !!response.userProfile;
}
